import {
    action,
    noop,
    die,
    isFunction,
    Annotation,
    createFlowAnnotation,
    decorateFlow20223_,
    assign
} from "../internal"
import { createDecoratorAnnotation, type DecoratorAnnotation } from "./decoratorannotation"
import type { ClassMethodDecorator } from "../types/decorator_fills"

export const FLOW = "flow"

let generatorId = 0

export class FlowCancellationError extends Error {
    constructor() {
        super("FLOW_CANCELLED")
        Object.setPrototypeOf(this, new.target.prototype)
        this.name = "FlowCancellationError"
    }

    toString() {
        return `Error: ${this.message}`
    }
}

export function isFlowCancellationError(error: Error) {
    return error instanceof FlowCancellationError
}

export type CancellablePromise<T> = Promise<T> & { cancel(): void }

function createFlowDecoratorAnnotation(
    annotation: Annotation
): DecoratorAnnotation<ClassMethodDecorator> {
    return createDecoratorAnnotation(annotation, decorateFlow20223_)
}

interface Flow extends Annotation, ClassMethodDecorator {
    <R, Args extends any[]>(
        generator: (...args: Args) => Generator<any, R, any> | AsyncGenerator<any, R, any>,
        context?: never
    ): (...args: Args) => CancellablePromise<R>
}

const flowAnnotation = createFlowAnnotation("flow")
const flowBoundAnnotation = createFlowAnnotation("flowBound", { bound: true })

export const flow: Flow = assign(
    function flow(arg1, arg2?) {
        if (arg2 && typeof arg2.kind === "string") {
            return decorateFlow20223_(flowAnnotation, arg1, arg2)
        }

        // flow(fn)
        if (__DEV__ && arguments.length !== 1) {
            die(`Flow expects single argument with generator function`)
        }
        const generator = arg1
        const name = generator.name || (__DEV__ ? "<unnamed flow>" : "flow")

        // Implementation based on https://github.com/tj/co/blob/master/index.js
        const res = function () {
            const ctx = this
            const args = arguments
            const runId = __DEV__ ? ++generatorId : 0
            const gen = action(
                __DEV__ ? `${name} - runid: ${runId} - init` : name,
                generator
            ).apply(ctx, args)
            let rejector: (error: any) => void
            let pendingPromise: CancellablePromise<any> | undefined = undefined

            const promise = new Promise(function (resolve, reject) {
                let stepId = 0
                rejector = reject

                function onFulfilled(res: any) {
                    pendingPromise = undefined
                    let ret
                    try {
                        ret = action(
                            __DEV__ ? `${name} - runid: ${runId} - yield ${stepId++}` : name,
                            gen.next
                        ).call(gen, res)
                    } catch (e) {
                        return reject(e)
                    }

                    next(ret)
                }

                function onRejected(err: any) {
                    pendingPromise = undefined
                    let ret
                    try {
                        ret = action(
                            __DEV__ ? `${name} - runid: ${runId} - yield ${stepId++}` : name,
                            gen.throw!
                        ).call(gen, err)
                    } catch (e) {
                        return reject(e)
                    }
                    next(ret)
                }

                function next(ret: any) {
                    if (isFunction(ret?.then)) {
                        // an async iterator
                        ret.then(next, reject)
                        return
                    }
                    if (ret.done) {
                        return resolve(ret.value)
                    }
                    pendingPromise = Promise.resolve(ret.value) as any
                    return pendingPromise!.then(onFulfilled, onRejected)
                }

                onFulfilled(undefined) // kick off the process
            }) as any

            const cancelActionName = __DEV__ ? `${name} - runid: ${runId} - cancel` : name
            promise.cancel = action(cancelActionName, function () {
                try {
                    if (pendingPromise) {
                        cancelPromise(pendingPromise)
                    }
                    // Finally block can return (or yield) stuff..
                    const res = gen.return!(undefined as any)
                    // eat anything that promise would do, it's cancelled!
                    const yieldedPromise = Promise.resolve(res.value)
                    yieldedPromise.then(noop, noop)
                    cancelPromise(yieldedPromise) // maybe it can be cancelled :)
                    // reject our original promise
                    rejector(new FlowCancellationError())
                } catch (e) {
                    rejector(e) // there could be a throwing finally block
                }
            })
            return promise
        }
        res.isMobXFlow = true
        return res
    } as any,
    flowAnnotation
)

export const flowBound = createFlowDecoratorAnnotation(flowBoundAnnotation)

function cancelPromise(promise) {
    if (isFunction(promise.cancel)) {
        promise.cancel()
    }
}

export function flowResult<T>(
    result: T
): T extends Generator<any, infer R, any>
    ? CancellablePromise<R>
    : T extends CancellablePromise<any>
    ? T
    : never {
    return result as any // just tricking TypeScript :)
}

export function isFlow(fn: any): boolean {
    return fn?.isMobXFlow === true
}
